import React, { useMemo, useState, useCallback } from "react";
import { Grid, GridItem, Spinner } from "nr1";
import { useTranslation } from "react-i18next";
import useStoreStatusLogic from "./useStoreStatusLogic";
import StoreFilterToolbar from "./StoreFilterToolbar";
import StoreSelectorDropdown from "./StoreSelectorDropdown";
import StoreSummaryHeader from "./StoreSummaryHeader";
import StoreHexGrid from "./StoreHexGrid";
import StorePaginationControls from "./StorePaginationControls";
import ModalBox from "./ModalBox";
import Breadcrumb from "./Breadcrumb";
import DisplayStoreLineChart from "./DisplayStoreLineChart";

// Stable fallbacks (avoid recreating on each render)
const EMPTY_COUNTS = Object.freeze({ total: 0, red: 0, amber: 0, green: 0 });
const EMPTY_ARRAY = Object.freeze([]);
const DEFAULT_MODAL_ACCOUNT_ID = 4091918;

function getStoreId(store) {
  return store?.id ?? store?.storeId ?? store?.name ?? store?.storeName ?? "";
}
function getStoreName(store) {
  return store?.name ?? store?.storeName ?? String(getStoreId(store) || "Store");
}

const StoreStatusDashboard = ({
  source,
  selectedService,
  onGoHome,
  onBackToServicesList,
  onBack,
  onStoreClick,
  currentPage,
  setCurrentPage,
  stores,
  setStores,
  filter,
  setFilter,
}) => {
  const { t } = useTranslation("storeStatusHexGrid");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalCard, setModalCard] = useState(null);
  const [modalAccountId, setModalAccountId] = useState(null);
  const [isTrendOpen, setIsTrendOpen] = useState(false);
  const {
    loading,
    selectedStore,
    setSelectedStore,
    pageSize,
    setPageSize,
    counts,
    storeOptions,
    filteredStores,
    currentStores,
    totalPages,
    visibleStart,
    visibleEnd,
  } = useStoreStatusLogic({
    stores,
    setStores,
    filter,
    setFilter,
    currentPage,
    setCurrentPage,
    source,
    selectedService,
  });

  const serviceLabel = selectedService?.label || selectedService?.key || "";

  // Stable fallbacks to avoid unnecessary renders in memoized children
  const effectiveCounts = counts ?? EMPTY_COUNTS;
  const effectiveCurrentStores = useMemo(
    () => (Array.isArray(currentStores) ? currentStores : EMPTY_ARRAY),
    [currentStores]
  );

  const breadcrumbSegments = useMemo(() => {
    const segs = [{ label: t("home", { defaultValue: "Home" }), onClick: onBack }];
    if (source === "stores") {
      segs.push({ label: t("Stores", { defaultValue: "Stores" }) });
    } else if (source === "services") {
      segs.push({
        label: t("services", { defaultValue: "Services" }),
        onClick: onBackToServicesList || onBack,
      });
      if (serviceLabel) segs.push({ label: serviceLabel });
    }
    return segs;
  }, [t, source, serviceLabel, onGoHome, onBackToServicesList, onBack]);

  // const handleBack = useCallback(() => {
  //   if (typeof onBack === "function") return onBack();
  //   if (typeof onBackToServicesList === "function") return onBackToServicesList();
  // }, [onBack, onBackToServicesList]);

  const handleStoreClick = useCallback(
    (store) => {
      const canOpenModal =
        source === "services" &&
        selectedService?.modal &&
        selectedService?.modalQuery;

       // console.log('handleStoreClick --> L152',canOpenModal, selectedService, store);

      if (canOpenModal) {
        const query =
          typeof selectedService.modalQuery === "function"
            ? selectedService.modalQuery(store)
            : selectedService.modalQuery;

        setModalCard({
          ...selectedService,
          modalQuery: query,
          storeId: store,
          storeName: getStoreName(store),
        });
        setModalAccountId(selectedService?.accountId ?? DEFAULT_MODAL_ACCOUNT_ID);
        setModalOpen(true);
        //console.log('handleStoreClick --> L121',modalCard);
      }

      if (typeof onStoreClick === "function") onStoreClick(store);
    },
    [onStoreClick, selectedService, source]
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setModalCard(null);
    setModalAccountId(null);
  }, []);

  return (
    <Grid className="wrapper">
      <div className="page-stack">
        <Breadcrumb segments={breadcrumbSegments} /> 
      </div>

      <GridItem columnSpan={12}>
         <div className="oh-top-card-store">
         <button
                            className="oh-card__header"
                            onClick={() => setIsTrendOpen((v) => !v)}
                            aria-expanded={isTrendOpen}
                          >
                           Historical Trendline (Store Health){" "}
                            {/*isTrendOpen ? "Collapse" : "Expand" */}
                          </button>
        
                          {isTrendOpen && (
                            <div className="oh-card__body">
                              <DisplayStoreLineChart />
                            </div>
                          )}

       </div>
      </GridItem>

      <GridItem columnSpan={12}>
        

        <StoreFilterToolbar
          filter={filter}
          setFilter={setFilter}
          counts={effectiveCounts}
          pageSize={pageSize}
          setPageSize={setPageSize}
          setSelectedStore={setSelectedStore}
          onBackToServicesList={onBackToServicesList}
          onBack={onBack}
          t={t}
        />
  
        <StoreSelectorDropdown
          storeOptions={storeOptions}
          selectedStore={selectedStore}
          setSelectedStore={setSelectedStore}
        />

        {loading ? (
          <div className="loading-container" aria-live="polite">
            <Spinner />
            <p>{t("loading", { defaultValue: "Loading..." })}</p>
          </div>
        ) : (
          <>
            <StoreSummaryHeader
              visibleStart={visibleStart}
              visibleEnd={visibleEnd}
              filteredStores={filteredStores}
              counts={effectiveCounts}
              filter={filter}
              selectedStore={selectedStore}
              t={t}
            />

          

            {effectiveCurrentStores.length === 0 ? (
              <div className="empty-state" role="status" aria-live="polite">
                {t("noResults", { defaultValue: "No stores to display." })}
              </div>
            ) : (
              <StoreHexGrid
                currentStores={effectiveCurrentStores}
                onStoreClick={handleStoreClick}
                t={t}
              />
            )}

            <StorePaginationControls
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              t={t}
            />
          </>
        )}
      </GridItem>

      <ModalBox
        isOpen={modalOpen}
        onClose={handleCloseModal}
        card={modalCard}
        accountId={modalAccountId}
      />
    </Grid>
  );
};

StoreStatusDashboard.displayName = "StoreStatusDashboard";
export default React.memo(StoreStatusDashboard);